import { isNumeric } from '../utils/isNumeric';
import { scaleFont } from '../utils/scaleFont';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';
import { Text } from './Text';

const SUBSCRIPT_SCALE = 0.7;

export class Subscript extends MathObject {
  base: Multi;
  sub: Multi;

  constructor(line: Line, base: Multi, sub: Multi) {
    super();

    this.t = 'subscript';
    this.gmmName = 'subscript';

    this.base = base;
    this.base.gmmName = 'base multi';
    this.base.parent = this;
    this.sub = sub;
    this.sub.gmmName = 'sub multi';
    this.sub.parent = this;
    this.line = line;

    this.children.push(this.base);
    this.children.push(this.sub);

    scaleFont(
      this.sub,
      (this.sub.getValueChildren()[0] as Text).font,
      SUBSCRIPT_SCALE
    );
  }

  getPaintedCharCount(): number {
    return this.base.getPaintedCharCount() + this.sub.getPaintedCharCount();
  }

  setEditable(b: boolean): void {
    this.base.setEditable(b);
    this.sub.setEditable(b);
  }

  setFont(f: string): void {
    this.base.setFont(f);
    scaleFont(this.sub, f, SUBSCRIPT_SCALE);
  }

  swapString(old: string, replacer: string): void {
    this.base.swapString(old, replacer);
    this.sub.swapString(old, replacer);
  }

  hasDigitBase(): boolean {
    if (this.base.children.length > 1) return false;
    const t = this.base.children[0] as Text;

    if (!(t instanceof Text)) return false;

    return isNumeric(t.text);
  }

  setBaseAndSub(base: Multi, sub: Multi): void {
    this.children = [];
    this.children.push(base);
    this.children.push(sub);
    this.base = base;
    this.sub = sub;
    base.parent = this;
    sub.parent = this;
    scaleFont(
      this.sub,
      (this.sub.getValueChildren()[0] as Text).font,
      SUBSCRIPT_SCALE
    );
  }

  setLine(line: Line): void {
    this.line = line;
    this.base.setLine(line);
    this.sub.setLine(line);
  }

  toXML(): string {
    return (
      '<s><b>' +
      this.base.toXML() +
      '</b><sub>' +
      this.sub.toXML() +
      '</sub></s>'
    );
  }

  grabFocus(d: Direction): void {
    if (d === 'left') {
      this.sub.grabFocus(d);
    } else {
      this.base.grabFocus(d);
    }
  }

  unsureWhichToFocus(): void {
    const b = this.base.children[0] as MathObject;

    if (b instanceof Text && b.text.length === 0) {
      this.base.grabFocus();
    } else {
      this.sub.grabFocus();
    }
  }

  buildSizeRecursive(): void {
    this.base.buildSizeRecursive();
    this.sub.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    const w = this.base.viewportW + this.sub.viewportW;
    const h = this.base.viewportH + 0.5 * this.sub.viewportH;

    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.base.viewportY + this.base.getVertToBaseline();
  }

  setPositionsOfChildren(): void {
    this.base.viewportY = 0.25 * this.sub.viewportH;
    this.sub.viewportY = 0.75 * this.base.viewportH;
    this.sub.viewportX = this.base.viewportW;
  }

  childSizeChanged(): void {
    this.buildSize();
    (this.parent as MathObject)?.childSizeChanged();
  }

  add(tile: MathObject, sub: any): void {
    if (!sub) {
      this.base.add(tile);
    } else {
      this.sub.add(tile);
    }
  }
}

export function buildSubscript(
  problemJS: MultiDependencies,
  line: Line,
  base?: Multi,
  sub?: Multi
): Subscript {
  return new Subscript(
    line,
    base || buildDefaultMulti(problemJS, line),
    sub || buildDefaultMulti(problemJS, line)
  );
}
